import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { LinkContext } from "../../LinkProvider";
import AnimatedCTA from "../AnimatedCTA";
import bannerBg from "../../img/about/banner-bg.png";
import bannerGrid from "../../img/about/banner-grid.svg";
import bannerHero from "../../img/about/banner-hero.png";

const BULLETS = ["about.banner.b1", "about.banner.b2", "about.banner.b3", "about.banner.b4"];
const REVEAL_COLS = 5;
const REVEAL_ROWS = 4;

const REVEAL_TILES = Array.from({ length: REVEAL_COLS * REVEAL_ROWS }, (_, index) => {
  const col = index % REVEAL_COLS;
  const row = Math.floor(index / REVEAL_COLS);
  const order = (index * 37) % (REVEAL_COLS * REVEAL_ROWS);
  const horizontal = (index + row) % 2 === 0;

  return {
    id: index,
    className: horizontal ? "banner-reveal-x" : "banner-reveal-y",
    style: {
      left: `${(col / REVEAL_COLS) * 100}%`,
      top: `${(row / REVEAL_ROWS) * 100}%`,
      width: `${100 / REVEAL_COLS}%`,
      height: `${100 / REVEAL_ROWS}%`,
      animationDelay: `${80 + order * 60}ms`,
      transformOrigin: horizontal
        ? ((index + row) % 2 === 0 ? "left" : "right")
        : ((index + col) % 2 === 0 ? "top" : "bottom"),
    },
  };
});

function Banner() {
  const { t } = useTranslation();
  const { link } = useContext(LinkContext);

  return (
    <section className="relative bg-[#020202] overflow-hidden pt-[56px] xl:pt-0 xl:h-[955px]">

      {/* Desktop 1512px visual container */}
      <div className="hidden xl:block absolute left-1/2 -translate-x-1/2 top-0 w-[1512px] h-[955px] overflow-clip pointer-events-none">
        <div className="absolute left-0 top-[6px] w-[1512px] h-[949px] overflow-hidden">
          <img src={bannerBg} alt="" aria-hidden className="absolute left-0 max-w-none w-full" style={{ height: "119.49%", top: "-0.05%" }} />
        </div>
        <img src={bannerGrid} alt="" aria-hidden className="absolute inset-0 w-full h-full" />
        <img src={bannerHero} alt="" aria-hidden className="absolute max-w-none object-cover" style={{ left: "621px", top: "94px", width: "891px", height: "890px" }} />
      </div>

      {/* Mobile background */}
      <div className="xl:hidden absolute inset-0 overflow-hidden pointer-events-none">
        <img src={bannerBg} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <img src={bannerGrid} alt="" aria-hidden className="absolute inset-0 w-full h-full" />
      </div>

      {/* Left gradient fade desktop */}
      <div className="hidden xl:block absolute inset-0 pointer-events-none z-[2]"
        style={{ background: "linear-gradient(to right, #020202 0%, #020202 30%, rgba(2,2,2,0.7) 50%, rgba(2,2,2,0) 70%)" }} />

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-[120px] pointer-events-none z-[2]"
        style={{ background: "linear-gradient(to top, #020202 0%, rgba(2,2,2,0) 100%)" }} />

      {/* Animated tiles (desktop only) */}
      <div className="hidden xl:block absolute top-[106px] left-1/2 -translate-x-1/2 z-[3] w-full max-w-[1220px] h-[744px] pointer-events-none overflow-hidden">
        {REVEAL_TILES.map((tile) => (
          <span key={tile.id} className={`banner-reveal-tile ${tile.className}`} style={tile.style} />
        ))}
      </div>

      {/* ── Text content ── */}
      <div className="relative z-[4] glcontainer">

        {/* Desktop */}
        <div className="hidden xl:flex flex-col justify-between pt-[145px] h-[838px] w-[703px]">
          <div className="flex flex-col gap-[20px]">
            <p className="font-['Inter'] font-medium text-[14px] text-[#636363] tracking-[1.4px] uppercase leading-[1.2]">
              {t("about.banner.subtitle")}
            </p>
            <h1 className="font-['Montserrat'] font-black text-[64px] text-[#e1e1e1] tracking-[-1.28px] leading-[1.2] max-w-[707px]">
              {t("about.banner.title")}
            </h1>
          </div>
          <ul className="flex flex-col gap-[6px] list-disc ps-[25px]">
            {BULLETS.map((key) => (
              <li key={key} className="font-['Montserrat'] font-extrabold text-[17px] text-white tracking-[-0.17px] uppercase leading-normal">
                {t(key)}
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-[46px] w-[488px]">
            <p className="font-['Inter'] font-normal text-[16px] text-[#bebebe] leading-[1.4]">
              {t("about.banner.desc")}
            </p>
            <AnimatedCTA href={link}>{t("about.banner.cta")}</AnimatedCTA>
          </div>
        </div>

        {/* Mobile */}
        <div className="xl:hidden flex flex-col gap-[20px] pb-[40px]">
          <p className="font-['Inter'] font-medium text-[12px] text-[#636363] tracking-[1.4px] uppercase leading-[1.2]">
            {t("about.banner.subtitle")}
          </p>
          <h1 className="font-['Montserrat'] font-black text-[28px] text-[#e1e1e1] tracking-[-1.28px] leading-[1.2]">
            {t("about.banner.title")}
          </h1>
          <ul className="flex flex-col gap-[6px] list-disc ps-[21px]">
            {BULLETS.map((key) => (
              <li key={key} className="font-['Montserrat'] font-extrabold text-[14px] text-white tracking-[-0.17px] uppercase leading-normal">
                {t(key)}
              </li>
            ))}
          </ul>
          <p className="font-['Inter'] font-normal text-[14px] text-[#bebebe] leading-[1.4]">
            {t("about.banner.desc")}
          </p>
          <AnimatedCTA href={link} mobile full>{t("about.banner.cta")}</AnimatedCTA>
          <img src={bannerHero} alt="" aria-hidden className="relative z-[1] w-[320px] mx-auto pointer-events-none" />
        </div>

      </div>
    </section>
  );
}

export default Banner;
